import React, { useEffect, useReducer } from 'react';

/**
 * FindOverlay — Walter 2026-06-16. Draws the find-bar highlights.
 *
 * A <textarea> can't show multiple highlights (its only "highlight" is the
 * selection, which goes invisible once the find input takes focus — Walter:
 * "nothing is highlighted"). So we draw a transparent mirror layer ON TOP of
 * each matching textarea: same font / padding / wrapping, all text
 * transparent, with a translucent `<mark>` behind each match so the real
 * textarea text shows through with a colored wash. ALL matches highlight at
 * once; the current one is stronger. Independent of focus, so the find input
 * keeps the keyboard while every hit stays lit.
 *
 * The mirror is clipped to the textarea's box and offset by its scrollTop, so
 * internal scrolling and page scrolling both stay aligned (re-measured on
 * scroll / resize / a slow safety tick).
 */

// Styles copied from the textarea so the mirror wraps text identically.
// NOTE: borders are intentionally NOT copied — the overlay container is
// positioned at the textarea's PADDING box (inset by the border) and sized to
// clientWidth/clientHeight (which EXCLUDE the scrollbar), so the mirror's text
// column matches the textarea's exactly even when a vertical scrollbar is
// present. Copying the border (and using the full rect width) was the cause of
// the "highlights off by a line on long lists" drift (Walter 2026-06-16).
const COPY = [
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant',
  'letterSpacing', 'lineHeight', 'textTransform', 'wordSpacing', 'textIndent',
  'whiteSpace', 'wordBreak', 'overflowWrap', 'tabSize',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
];

export default function FindOverlay({ query, matches, currentIndex }) {
  const [, tick] = useReducer((n) => n + 1, 0);

  // Keep the overlays glued to the textareas as things move/scroll/resize.
  useEffect(() => {
    if (!query) return undefined;
    const onMove = () => tick();
    // capture:true so we also catch scrolling INSIDE the textareas + canvas.
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    const id = setInterval(onMove, 350); // safety for drags / layout shifts
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      clearInterval(id);
    };
  }, [query]);

  if (!query || !matches || matches.length === 0) return null;

  // Group matches by their textarea, tagging the global current one.
  const byEl = new Map();
  matches.forEach((m, gi) => {
    // CodeMirror hits carry `view`, not `el` — there is no textarea to mirror,
    // and they're shown by selecting them in the editor instead (FindBar's
    // scrollToMatch). Skip them here rather than crash on a null key.
    if (!m.el) return;
    if (!byEl.has(m.el)) byEl.set(m.el, []);
    byEl.get(m.el).push({ start: m.start, end: m.end, current: gi === currentIndex });
  });

  const layers = [];
  let key = 0;
  for (const [el, ms] of byEl) {
    let rect;
    try { rect = el.getBoundingClientRect(); } catch { continue; }
    if (!rect || rect.width === 0 || rect.height === 0) continue;

    const cs = window.getComputedStyle(el);
    const bL = parseFloat(cs.borderLeftWidth) || 0;
    const bT = parseFloat(cs.borderTopWidth) || 0;
    // Mirror = a border-box div the width of the textarea's CONTENT+padding
    // area (clientWidth, which excludes the scrollbar). With the textarea's
    // paddings copied, its text column equals the textarea's → identical wrap.
    const mirrorStyle = {
      position: 'absolute', left: 0, top: 0,
      width: el.clientWidth,
      boxSizing: 'border-box',
      margin: 0, color: 'transparent',
    };
    COPY.forEach((k) => { mirrorStyle[k] = cs[k]; });
    // Walter 2026-08-21: same iOS inflation trap as SyntaxBackdrop — the
    // mirror must never be inflated independently of the textarea it mirrors,
    // and getComputedStyle can't see that it has been.
    mirrorStyle.webkitTextSizeAdjust = '100%';

    // Split the text into plain + marked segments.
    const value = el.value || '';
    ms.sort((a, b) => a.start - b.start);
    const segs = [];
    let cur = 0;
    ms.forEach((m) => {
      if (m.start > cur) segs.push({ t: value.slice(cur, m.start) });
      segs.push({ t: value.slice(m.start, m.end), mark: true, isCurrent: m.current });
      cur = m.end;
    });
    if (cur < value.length) segs.push({ t: value.slice(cur) });

    layers.push(
      <div
        key={key++}
        aria-hidden="true"
        style={{
          // Position at the textarea's PADDING box (inset past the border) and
          // size to the client area (excludes border + scrollbar) so the mirror
          // aligns even when the textarea has a vertical scrollbar.
          position: 'fixed',
          left: rect.left + bL, top: rect.top + bT,
          width: el.clientWidth, height: el.clientHeight,
          overflow: 'hidden', pointerEvents: 'none', zIndex: 2340,
        }}
      >
        <div style={{ ...mirrorStyle, transform: `translateY(${-el.scrollTop}px)` }}>
          {segs.map((s, i) => (s.mark ? (
            <mark
              key={i}
              style={{
                // Translucent so the real (opaque) textarea text shows through.
                background: s.isCurrent ? 'rgba(232,98,44,0.78)' : 'rgba(232,98,44,0.30)',
                color: 'transparent',
                borderRadius: 2,
                boxShadow: s.isCurrent ? '0 0 0 1px rgba(232,98,44,0.9)' : 'none',
              }}
            >
              {s.t}
            </mark>
          ) : (
            <span key={i}>{s.t}</span>
          )))}
        </div>
      </div>,
    );
  }

  return <>{layers}</>;
}
